import { TodoItemSchema } from '@terra/shared-contracts-smoke-test-history';
import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import { z } from 'zod';
import { parseChatHistory, type PlanTodoItem } from '../types';
import { normalizeToolName } from './tool-config.helpers';
import type { TracePhase } from './trace-tool-filters';

const PlanArraySchema = z.array(TodoItemSchema);

const TODO_TOOL_NAMES = new Set(['todo-write', 'write-todos']);

function mapTodoStatus({
  status,
  state,
}: {
  status?: unknown;
  state?: unknown;
}): PlanTodoItem['status'] | undefined {
  const raw =
    typeof status === 'string'
      ? status
      : typeof state === 'string'
        ? state
        : undefined;
  if (
    raw === 'pending' ||
    raw === 'completed' ||
    raw === 'in_progress' ||
    raw === 'cancelled'
  ) {
    return raw;
  }
  return undefined;
}

function mapTodosToPlanItems(
  todos: Array<{
    id: string;
    content: string;
    status?: unknown;
    state?: unknown;
  }>,
): PlanTodoItem[] | undefined {
  const mapped = todos
    .map((todo) => {
      const status = mapTodoStatus(todo);
      if (!status) {
        return null;
      }
      return {
        id: todo.id,
        content: todo.content,
        status,
      };
    })
    .filter((todo): todo is PlanTodoItem => todo !== null);

  const parseResult = PlanArraySchema.safeParse(mapped);
  return parseResult.success && parseResult.data.length > 0
    ? parseResult.data
    : undefined;
}

function extractLatestCoPilotPlanFromDataMessages(
  messages: TerraUIMessage[],
): PlanTodoItem[] | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    const parts = msg.parts ?? [];

    for (let j = parts.length - 1; j >= 0; j--) {
      const part = parts[j];
      if (
        part.type !== TerraUIMessageDataPartName ||
        part.data?.type !== DataMessageTypeEnum.Todos ||
        !Array.isArray(part.data.todos)
      ) {
        continue;
      }

      const plan = mapTodosToPlanItems(part.data.todos);
      if (plan) {
        return plan;
      }
    }
  }

  return undefined;
}

function extractLatestCoPilotPlanFromToolCalls(
  messages: TerraUIMessage[],
  tracePhase: TracePhase,
): PlanTodoItem[] | undefined {
  const allMessages = parseChatHistory(messages, tracePhase);

  for (let i = allMessages.length - 1; i >= 0; i--) {
    const msg = allMessages[i];
    for (const content of msg.content) {
      if (content.type !== 'tool_call') {
        continue;
      }

      const normalizedName = normalizeToolName(content.toolCall.name);
      if (!TODO_TOOL_NAMES.has(normalizedName)) {
        continue;
      }

      if (content.toolCall.metadata?.fullPlan) {
        const parseResult = PlanArraySchema.safeParse(
          content.toolCall.metadata.fullPlan,
        );
        if (parseResult.success && parseResult.data.length > 0) {
          return parseResult.data;
        }
      }

      if (Array.isArray(content.toolCall.args?.todos)) {
        const plan = mapTodosToPlanItems(
          content.toolCall.args.todos as Array<{
            id: string;
            content: string;
            status?: unknown;
            state?: unknown;
          }>,
        );
        if (plan) {
          return plan;
        }
      }
    }
  }

  return undefined;
}

export function extractLatestCoPilotPlan(
  messages: TerraUIMessage[],
  tracePhase: TracePhase = 'co-pilot',
): PlanTodoItem[] | undefined {
  return (
    extractLatestCoPilotPlanFromDataMessages(messages) ??
    extractLatestCoPilotPlanFromToolCalls(messages, tracePhase)
  );
}
